import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useMeta } from "@/lib/meta";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import { Rocket, Zap, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function MerchantBoost() {
  const { t, lang } = useLang();
  const { refresh } = useAuth();
  const { meta } = useMeta();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const mountRef = useRef(null);
  const mountedOrder = useRef(null);

  const paymentsEnabled = meta ? !!meta.payments_enabled : null;

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/merchant/boost"),
        api.get("/boost/packages"),
      ]);
      setData(s.data);
      setPackages(p.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    if (paymentsEnabled === false) return; // payments disabled — skip data load
    if (paymentsEnabled === true) load();
  }, [paymentsEnabled]);

  // Check for redirect from Moyasar (?id=...&status=paid)
  useEffect(() => {
    if (paymentsEnabled === false) return;
    const sp = new URLSearchParams(window.location.search);
    const pid = sp.get("id");
    if (pid) {
      (async () => {
        try {
          const { data } = await api.get(`/payments/${pid}/verify`);
          if (data.paid) {
            toast.success("Payment confirmed — credits added");
            await refresh();
            load();
          } else {
            toast.error(`Payment ${data.status || "incomplete"}`);
          }
        } catch (e) { toast.error(errMsg(e)); }
        finally { window.history.replaceState(null, "", "/merchant/boost"); }
      })();
    }
  }, []);

  const startCheckout = async (key) => {
    setSelectedPackage(key);
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/boost/checkout", { package: key });
      setPendingOrder(data);
    } catch (e) { toast.error(errMsg(e)); setSelectedPackage(null); }
    finally { setBusy(false); }
  };

  // Mount Moyasar form when pendingOrder available
  useEffect(() => {
    if (!pendingOrder || !mountRef.current) return;
    if (mountedOrder.current === pendingOrder.order_id) return;
    if (!window.Moyasar) {
      toast.error("Moyasar script not loaded. Refresh the page.");
      return;
    }
    mountedOrder.current = pendingOrder.order_id;
    const pk = process.env.REACT_APP_MOYASAR_PUBLISHABLE_KEY;
    try {
      window.Moyasar.init({
        element: mountRef.current,
        amount: pendingOrder.amount,
        currency: "SAR",
        description: pendingOrder.description,
        publishable_api_key: pk,
        callback_url: `${window.location.origin}/merchant/boost`,
        methods: ["creditcard", "applepay", "stcpay"],
        on_completed: async (payment) => {
          try {
            await api.post("/payments/link", {
              order_id: pendingOrder.order_id,
              payment_id: payment.id,
              status: payment.status,
            });
          } catch (_e) { /* logged server-side */ }
        },
      });
    } catch (e) {
      toast.error("Could not load checkout: " + (e.message || e));
    }
  }, [pendingOrder]);

  const cancelCheckout = () => {
    setPendingOrder(null);
    setSelectedPackage(null);
    mountedOrder.current = null;
    if (mountRef.current) mountRef.current.innerHTML = "";
  };

  // Avoid unused warnings — navigate kept for future use
  void navigate; void Sparkles;

  // GATE — paid Boost is hidden while payments are disabled
  if (paymentsEnabled === false) {
    const ar = lang === "ar";
    return (
      <div className="app-shell pb-24" data-testid="merchant-boost-page">
        <TopBar title={ar ? "تعزيز العروض" : "Boost"} />
        <div className="px-5 pt-6">
          <div className="card-surface p-6 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-emerald-700" data-testid="boost-gate">
            <div className="h-14 w-14 rounded-2xl bg-white/10 grid place-items-center mb-4">
              <Rocket className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl font-extrabold leading-tight">
              {ar ? "تعزيز العروض — قريبًا" : "Boost — coming soon"}
            </h1>
            <p className="mt-3 text-sm opacity-90 leading-relaxed">
              {ar
                ? "ميزة التعزيز المدفوع ستتوفر قريبًا. حتى ذلك الحين، يمكنك إرسال عروض غير محدودة مجانًا."
                : "Paid Boost will be available soon. Until then, send unlimited offers for free."}
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3">
            <Link to="/merchant" data-testid="boost-back-home" className="btn-primary w-full">
              {ar ? "العودة إلى الرئيسية" : "Back to dashboard"}
            </Link>
            <Link to="/merchant/feed" data-testid="boost-go-feed" className="btn-secondary w-full">
              {ar ? "تصفّح الطلبات المطابقة" : "Browse matched requests"}
            </Link>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-shell pb-24" data-testid="merchant-boost-page">
      <TopBar title="Boost credits" />
      <div className="px-5 pt-4">
        <div className="card-surface p-5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-emerald-700">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/10 grid place-items-center">
              <Rocket className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Your credits</div>
              <div className="text-3xl font-extrabold mt-0.5" data-testid="boost-balance">{data?.credits ?? 0}</div>
              <div className="text-xs opacity-80 mt-0.5">{data?.boosted_offers ?? 0} offers boosted so far</div>
            </div>
          </div>
          <p className="text-sm mt-4 opacity-90">Boosted offers appear at the top of the customer&apos;s list — earlier views, more wins.</p>
        </div>

        {!pendingOrder && (
          <>
            <h3 className="font-display text-lg font-semibold mt-6">Buy boost credits</h3>
            <div className="grid grid-cols-1 gap-3 mt-3">
              {Object.entries(packages).map(([key, pkg]) => (
                <button
                  key={key}
                  data-testid={`pkg-${key}`}
                  onClick={() => startCheckout(key)}
                  disabled={busy}
                  className={`card-surface p-4 flex items-center gap-3 text-start transition hover:border-emerald-300 ${selectedPackage === key ? "border-emerald-500" : ""}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{pkg.label}</div>
                    <div className="text-xs text-zinc-500">{pkg.credits} credits</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-emerald-700">{pkg.price_sar} SAR</div>
                    <div className="text-[10px] text-zinc-400">{(pkg.price_sar / pkg.credits).toFixed(1)} / credit</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {pendingOrder && (
          <div className="mt-6" data-testid="checkout-area">
            <div className="card-surface p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-display text-lg font-semibold">Checkout</div>
                  <div className="text-xs text-zinc-500">{pendingOrder.description}</div>
                </div>
                <button data-testid="cancel-checkout" onClick={cancelCheckout} className="h-8 w-8 rounded-full grid place-items-center hover:bg-zinc-100">
                  <X className="h-4 w-4 text-zinc-600" />
                </button>
              </div>
              <div className="mb-4 text-2xl font-bold">{(pendingOrder.amount / 100).toFixed(0)} SAR</div>
              <div ref={mountRef} className="mysr-form" data-testid="moyasar-mount" />
              <p className="text-[11px] text-zinc-400 mt-3 text-center">Powered by Moyasar — test mode. Use Moyasar test card: 4111 1111 1111 1111 · 12/30 · CVC 123</p>
            </div>
          </div>
        )}

        {data?.history?.length > 0 && (
          <div className="mt-6">
            <p className="label-caps mb-2">History</p>
            <div className="space-y-2">
              {data.history.map((h) => (
                <div key={h.order_id} className="card-surface p-3 flex items-center justify-between text-sm" data-testid={`history-${h.order_id}`}>
                  <div>
                    <div className="font-semibold capitalize">{h.package} · {h.credits} credits</div>
                    <div className="text-xs text-zinc-500">{new Date(h.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`pill ${h.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {h.status === "paid" ? <Check className="h-3 w-3" /> : null}
                    {h.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
